// ---------------------------------------------------------------------------
// Day/night cycle (#2). Fully procedural: drives the existing sun, sky, fog,
// hemisphere and ambient from a single time-of-day value. No assets — just
// numbers animated over time. Returns a 0..1 "night factor" each frame that the
// caller uses to ramp the point lights.
// ---------------------------------------------------------------------------
import * as THREE from "three";

// Key palette through the day.
const DAY_SKY = new THREE.Color(0x9fd3ff);
const NIGHT_SKY = new THREE.Color(0x0a1430);
const DUSK_SKY = new THREE.Color(0xff9e5e);
const GROUND = new THREE.Color(0x4a7a3a);
const SUN_NOON = new THREE.Color(0xfff4e0);
const SUN_WARM = new THREE.Color(0xff8a32);
const MOON = new THREE.Color(0x9fb4e6);
const WHITE = new THREE.Color(0xffffff);

export function smoothstep(a: number, b: number, x: number): number {
  const t = THREE.MathUtils.clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

// The CLEAR-WEATHER fog baseline. Set once by the constructor below; weather.ts
// owns fog.near/far per frame from then on (grading only ever SHRINKS them, so
// the CAM_FAR=170 / IM nearRadius contracts pinned to far=135 stay true).
export const FOG_NEAR = 60;
export const FOG_FAR = 135;

export type DayNight = {
  sun: THREE.DirectionalLight;
  // hemi/ambient are exposed for weather.ts's grading pass (it multiplies the
  // absolute intensities update() writes each frame — see src/weather.ts).
  // CONTRACT: update() never writes hemi.groundColor after construction —
  // weather.ts owns it per frame (snow whitening from a captured base).
  hemi: THREE.HemisphereLight;
  ambient: THREE.AmbientLight;
  /** The sun's live unit direction — refreshed by update() each frame (the
   *  moon in weather.ts rides exactly opposite it, no re-derived trig). */
  sunDir: THREE.Vector3;
  /** tod in [0,1): 0 = midnight, 0.25 = sunrise, 0.5 = noon, 0.75 = sunset.
   *  Returns the night factor (1 at midnight, 0 in full day). */
  update(tod: number, player: THREE.Vector3): number;
};

export function makeDayNight(scene: THREE.Scene): DayNight {
  // ---- Sun (the only shadow caster), tracks the player so its frustum stays put ----
  const sun = new THREE.DirectionalLight(0xffffff, 1.7);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  const sc = sun.shadow.camera as THREE.OrthographicCamera;
  sc.left = -60; sc.right = 60; sc.top = 60; sc.bottom = -60;
  sc.near = 1; sc.far = 220;
  scene.add(sun);
  scene.add(sun.target);

  const hemi = new THREE.HemisphereLight(DAY_SKY.getHex(), GROUND.getHex(), 0.6);
  scene.add(hemi);
  const ambient = new THREE.AmbientLight(0xffffff, 0.15);
  scene.add(ambient);

  // Clear-weather fog baseline (see FOG_NEAR/FOG_FAR above for the ownership
  // handoff); this module keeps owning fog.COLOR's source (slaved to the sky).
  scene.fog = new THREE.Fog(DAY_SKY.getHex(), FOG_NEAR, FOG_FAR);
  scene.background = DAY_SKY.clone();
  const sky = scene.background as THREE.Color;
  const dir = new THREE.Vector3();

  return {
    sun,
    hemi,
    ambient,
    sunDir: dir, // the same vector update() writes below — live by reference
    update(tod, player) {
      const az = (tod - 0.25) * Math.PI * 2; // 0 at sunrise, π/2 at noon, π at sunset
      const elev = Math.sin(az);
      const day = smoothstep(-0.12, 0.22, elev);          // 0 night → 1 full day
      // Warmth peaks while the sun sits near the horizon (dawn/dusk).
      const dusk = Math.max(0, 1 - Math.abs(elev) / 0.22) * smoothstep(-0.25, 0.0, elev);

      // Sun rides an arc east→west; constant z-tilt keeps shadows diagonal.
      dir.set(Math.cos(az), Math.max(elev, -0.2), 0.33).normalize();
      sun.position.copy(player).addScaledVector(dir, 95);
      sun.target.position.copy(player);
      sun.color.copy(SUN_NOON).lerp(SUN_WARM, dusk);
      sun.intensity = day * 1.7; // → 0 at night, so its shadow simply fades out

      // Sky + matched fog, with a warm dawn/dusk wash.
      sky.copy(NIGHT_SKY).lerp(DAY_SKY, day).lerp(DUSK_SKY, dusk * 0.6);
      (scene.fog as THREE.Fog).color.copy(sky);

      // Fill light follows the sky; a cool moonlit ambient takes over at night.
      hemi.color.copy(sky);
      hemi.intensity = 0.25 + day * 0.5;
      ambient.color.copy(MOON).lerp(WHITE, day);
      ambient.intensity = 0.16 + day * 0.12;

      return 1 - day;
    },
  };
}
